const ORDER_TYPES = ['NA', 'Market', 'Limit', 'SL', 'LIVE'];
const CLOSE_TYPES = ['NA', 'Auto Exit', 'Contract Expired', 'Delivery Square-Up', 'unknown'];

export function getSettlementRemark(order) {
  const manual = String(order?.manualRemark ?? '').trim();
  if (manual) return manual;
  return String(order?.autoRemark ?? '').trim();
}

export function isWeekSettlement(order) {
  return /Week P&L/i.test(getSettlementRemark(order));
}

function parseRemarkDate(label) {
  const trimmed = String(label ?? '').trim();
  const match = trimmed.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/);
  if (match) {
    const months = {
      jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
      jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
    };
    const day = Number(match[1]);
    const month = months[match[2].toLowerCase()];
    const year = Number(match[3]);
    if (month !== undefined) {
      const dt = new Date(year, month, day);
      if (!Number.isNaN(dt.getTime())) {
        return formatIsoDate(dt);
      }
    }
  }

  const ts = Date.parse(trimmed);
  if (!Number.isNaN(ts)) {
    return formatIsoDate(new Date(ts));
  }
  return null;
}

function formatIsoDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function weekRangeFromOrder(order) {
  const remark = getSettlementRemark(order);
  const match = remark.match(
    /Week P&L\s+(\d{1,2}-[A-Za-z]{3}-\d{4})\s+to\s+(\d{1,2}-[A-Za-z]{3}-\d{4})/i
  );
  if (match) {
    const start = parseRemarkDate(match[1]);
    const end = parseRemarkDate(match[2]);
    if (start && end) {
      return { start, end };
    }
  }

  const dateOnly = formatIsoDate(new Date(order.dateTime));
  const endDate = new Date(dateOnly);
  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - 6);
  return { start: formatIsoDate(startDate), end: dateOnly };
}

export function formatBillHeaderDate(isoDate) {
  const dt = new Date(`${isoDate}T00:00:00`);
  return dt.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).replace(/ /g, '-');
}

export function formatExpiryDate(isoDate) {
  const dt = new Date(`${isoDate}T00:00:00`);
  return dt.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function formatAmount(value) {
  return Number(value).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function mergeTradeClientBrokerageFields(client) {
  if (!client || typeof client !== 'object') return {};
  const merged = { ...client };
  if (client.FUTCOM_LOT && typeof client.FUTCOM_LOT === 'object') {
    Object.entries(client.FUTCOM_LOT).forEach(([key, value]) => {
      if (key === 'clientId' && client.clientId !== undefined) return;
      merged[key] = value;
    });
  }
  return merged;
}

function normalizeCfBf(value) {
  return String(value ?? '').trim().replace(/^["']|["']$/g, '').toUpperCase();
}

export function isCfBfRow(cfbf) {
  const normalized = normalizeCfBf(cfbf);
  return normalized === 'CF' || normalized === 'BF';
}

export function calculateOrderBrokerage(client, order) {
  let brokerage = Number(order?.brokerage ?? 0);
  let viewerBrokerBrokerage = 0;

  if (Number(order?.orderType) === 4 || order?.isLive) {
    return { brokerage: 0, viewerBrokerBrokerage: 0 };
  }

  const cfbf = normalizeCfBf(order?.CF_BF);
  if (cfbf === 'CF' || cfbf === 'BF') {
    return { brokerage: 0, viewerBrokerBrokerage: 0 };
  }

  let qty = Number(order?.orderQuantity ?? 0);
  const lots = Number(order?.orderLotQuantity ?? 0);
  let price = Number(order?.orderPrice ?? 0);
  if (String(order?.orderTransactionType) === '2' && order?.entryPrice != null && order.entryPrice !== '') {
    price = Number(order.entryPrice);
  }

  const exchange = order?.exchange ?? '';
  const stockName = order?.stockName ?? '';

  let template = null;
  let mode = null;

  if (exchange === 'FUTCOM') {
    if (Number(client?.FUTCOM) === 1) {
      const brtype = client?.BRType ?? 'LOT';
      const templateKey = `${stockName}_${brtype}`;
      if (client?.[templateKey]) {
        mode = 'LOT';
        template = typeof client[templateKey] === 'string'
          ? JSON.parse(client[templateKey])
          : client[templateKey];
      }
    } else if (Number(client?.FUTCOM) === 2 && client?.FUTCOM_PER) {
      mode = 'PER';
      template = typeof client.FUTCOM_PER === 'string'
        ? JSON.parse(client.FUTCOM_PER)
        : client.FUTCOM_PER;
    }
  } else if (exchange === 'FUTSTK' && client?.FUTSTK_PER) {
    mode = 'PER';
    template = typeof client.FUTSTK_PER === 'string'
      ? JSON.parse(client.FUTSTK_PER)
      : client.FUTSTK_PER;
  } else if (exchange === 'OPTSTK' && client?.OPTSTK_LOT) {
    mode = 'LOT';
    template = typeof client.OPTSTK_LOT === 'string'
      ? JSON.parse(client.OPTSTK_LOT)
      : client.OPTSTK_LOT;
  }

  if (!template || template.total === undefined) {
    return { brokerage, viewerBrokerBrokerage: 0 };
  }

  const totalRate = Number(template.total);
  if (mode === 'LOT') {
    brokerage = lots * totalRate;
  } else {
    brokerage = (qty * price * totalRate) / 100;
  }

  return { brokerage, viewerBrokerBrokerage: 0 };
}

export function appendLiveCloseRows(orders, lastPrices) {
  if (!Array.isArray(orders) || orders.length === 0) return [];

  const result = [...orders];
  const scriptWise = {};

  orders.forEach((order) => {
    if (!order?.stockId) return;
    const key = String(order.stockId);
    if (!scriptWise[key]) scriptWise[key] = [];
    scriptWise[key].push(order);
  });

  Object.entries(scriptWise).forEach(([stockId, stockOrders]) => {
    let netQty = 0;
    let netLots = 0;

    stockOrders.forEach((stockOrder) => {
      const qty = Number(stockOrder.orderQuantity ?? 0);
      const lots = Number(stockOrder.orderLotQuantity ?? 0);
      const side = Number(stockOrder.buy_sell ?? 0);
      if (side === 1) {
        netQty += qty;
        netLots += lots;
      } else if (side === 2) {
        netQty -= qty;
        netLots -= lots;
      }
    });

    if (Math.abs(netQty) < 0.0001) return;

    const liveSide = netQty > 0 ? 2 : 1;
    let livePrice = 0;
    const priceRow = lastPrices?.[stockId];
    if (priceRow) {
      livePrice = liveSide === 1
        ? Number(priceRow.sellPrice ?? 0)
        : Number(priceRow.buyPrice ?? 0);
    }

    const baseOrder = stockOrders[0];
    result.push({
      ...baseOrder,
      orderId: 999999999,
      exeDateTime: new Date().toISOString().slice(0, 19).replace('T', ' '),
      orderType: 4,
      closeType: 0,
      buy_sell: liveSide,
      orderQuantity: Math.abs(netQty),
      orderLotQuantity: Math.abs(netLots),
      orderPrice: livePrice,
      brokerage: 0,
      broker_brokerage: null,
      associatedOrderId: null,
      orderTransactionType: 2,
      isLive: true,
    });
  });

  return result;
}

export function groupOrdersByStock(orders) {
  const grouped = {};
  orders.forEach((order) => {
    if (!order?.stockId) return;
    const key = String(order.stockId);
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(order);
  });
  return grouped;
}

export function getOrderTypeLabel(order) {
  if (Number(order.orderType) === 4) {
    return ORDER_TYPES[4];
  }

  const cfbf = String(order.CF_BF ?? 'NA');
  if (cfbf !== 'NA') return cfbf;
  if (Number(order.closeType) === 0) {
    return ORDER_TYPES[Number(order.orderType)] ?? 'NA';
  }
  return CLOSE_TYPES[Number(order.closeType)] ?? 'unknown';
}

export function buildScriptBillData(client, orders) {
  const sorted = [...orders].sort((a, b) => Number(a.orderId) - Number(b.orderId));
  let totalBuyTrades = 0;
  let totalSellTrades = 0;
  let totalBuyPrice = 0;
  let buyPriceCount = 0;
  let totalSellPrice = 0;
  let sellPriceCount = 0;
  let totalNetAmount = 0;
  let totalGrossAmount = 0;
  let totalBrokerage = 0;

  const rows = sorted.map((order, index) => {
    let { brokerage: orderBrokerage } = calculateOrderBrokerage(client, order);
    if (Number(order.orderType) === 4 || order.isLive) {
      orderBrokerage = 0;
    }

    const qty = Number(order.orderQuantity ?? 0);
    const price = Number(order.orderPrice ?? 0);
    const grossLine = price * qty;
    const isBuy = Number(order.buy_sell) === 1;

    if (isBuy) {
      totalBuyTrades += qty;
      totalBuyPrice += price;
      buyPriceCount += 1;
      totalGrossAmount -= grossLine;
    } else {
      totalSellTrades += qty;
      totalSellPrice += price;
      sellPriceCount += 1;
      totalGrossAmount += grossLine;
    }

    const netAmount = isBuy
      ? grossLine + orderBrokerage
      : grossLine - orderBrokerage;

    if (isBuy) {
      totalNetAmount -= netAmount;
    } else {
      totalNetAmount += netAmount;
    }

    totalBrokerage += orderBrokerage;

    return {
      index: index + 1,
      order,
      orderBrokerage,
      grossLine,
      netAmount,
      isBuy,
      typeLabel: getOrderTypeLabel(order),
      isLive: Number(order.orderType) === 4 || Boolean(order.isLive),
      cfbf: normalizeCfBf(order.CF_BF ?? 'NA') || 'NA',
      isCfBf: isCfBfRow(order.CF_BF),
    };
  });

  return {
    rows,
    totals: {
      totalBuyTrades,
      totalSellTrades,
      avgBuyPrice: buyPriceCount ? totalBuyPrice / buyPriceCount : 0,
      avgSellPrice: sellPriceCount ? totalSellPrice / sellPriceCount : 0,
      totalGrossAmount,
      totalBrokerage,
      totalNetAmount,
    },
  };
}

export function buildGrandTotals(scriptBills) {
  let grandGrossAmount = 0;
  let grandBrokerage = 0;

  scriptBills.forEach((bill) => {
    grandGrossAmount += bill.totals.totalGrossAmount;
    grandBrokerage += bill.totals.totalBrokerage;
  });

  return {
    grandGrossAmount,
    grandBrokerage,
    grandNetAmount: grandGrossAmount - grandBrokerage,
  };
}
